import { useState } from 'react'
import { useLang } from '../../core/lang'
import LangKeys from '../../core/locales/keys'
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Wallet,
  BarChart3,
  BellRing,
  Share2,
  Building2,
  Settings,
  Receipt,
  LogOut,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from 'lucide-react'

// Custom Premium Box Icon (100% Safe, No Imports Needed)
const ItemsIcon = ({ size = 20, strokeWidth = 2, color = 'currentColor' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.29 7 12 12 20.71 7" />
    <line x1="12" y1="22" x2="12" y2="12" />
  </svg>
);

const PremiumMedalIcon = ({ size = 80 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ filter: 'drop-shadow(0 2px 6px rgba(245, 158, 11, 0.2))' }}
  >
    <defs>
      <linearGradient id="sidePremiumGold" x1="20" y1="20" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFE082" />
        <stop offset="30%" stopColor="#FFB300" />
        <stop offset="70%" stopColor="#FF8F00" />
        <stop offset="100%" stopColor="#FF6F00" />
      </linearGradient>

      <filter id="sideGoldGlow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="1.5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>

      <filter id="sideShadowDepth" x="-10%" y="-10%" width="120%" height="130%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#B45309" floodOpacity="0.25" />
      </filter>
    </defs>

    {/* Glowing background aura */}
    <circle cx="50" cy="50" r="38" fill="url(#sidePremiumGold)" opacity="0.08" />
    <circle cx="50" cy="50" r="32" fill="url(#sidePremiumGold)" opacity="0.12" />

    {/* Elegant floating gold rings */}
    <circle cx="50" cy="50" r="36" stroke="url(#sidePremiumGold)" strokeWidth="1.2" strokeDasharray="3 6" opacity="0.5" />
    <circle cx="50" cy="50" r="30" stroke="url(#sidePremiumGold)" strokeWidth="1.8" opacity="0.7" />

    {/* Futuristic modern 3-point crown */}
    <g filter="url(#sideShadowDepth)">
      <path d="M30 64 C30 64 35 66 50 66 C65 66 70 64 70 64 L68 58 C68 58 63 60 50 60 C37 60 32 58 32 58 Z" fill="url(#sidePremiumGold)" />
      <path d="M30 64 L33 50 L42 56 L50 36 L58 56 L67 50 L70 64 C70 64 65 62 50 62 C35 62 30 64 30 64 Z" fill="url(#sidePremiumGold)" />

      {/* Gem details */}
      <circle cx="40" cy="62.5" r="1.2" fill="#FFFFFF" opacity="0.9" />
      <circle cx="50" cy="63" r="1.6" fill="#FFFFFF" opacity="0.95" />
      <circle cx="60" cy="62.5" r="1.2" fill="#FFFFFF" opacity="0.9" />

      <path d="M50 28 L52 32 L50 36 L48 32 Z" fill="#FFFFFF" filter="url(#sideGoldGlow)" />
      <circle cx="50" cy="34" r="1.2" fill="#FFFFFF" />
      <circle cx="33" cy="48" r="1.2" fill="#FFFFFF" />
      <circle cx="67" cy="48" r="1.2" fill="#FFFFFF" />
    </g>

    {/* Luxury sparkles */}
    <g filter="url(#sideGoldGlow)">
      <path d="M74 30 L75.5 33 L79 34 L75.5 35 L74 38 L72.5 35 L69 34 L72.5 33 Z" fill="#FFE082" />
    </g>
    <g filter="url(#sideGoldGlow)">
      <path d="M24 50 L25 52 L28 52.5 L25 53 L24 55 L23 53 L20 52.5 L23 52 Z" fill="#FFE082" />
    </g>
  </svg>
);

const sidebarTranslations = {
  en: {
    premiumTitle: "Moxpal Premium",
    premiumDesc: "100% Ad-Free, GST Billing, Multi-device & more!",
    upgradeBtn: "Upgrade Now 🚀",
    memberTitle: "Premium Member",
    memberDesc: "Active Plan: {plan}",
    daysLeft: "{days} Days Left",
    tooltipFree: "Upgrade to Premium 👑",
    tooltipPremium: "Premium Active 🥇"
  },
  hi: {
    premiumTitle: "मोक्सपाल प्रीमियम",
    premiumDesc: "100% विज्ञापन-मुक्त, GST बिलिंग, मल्टी-डिवाइस!",
    upgradeBtn: "अभी अपग्रेड करें 🚀",
    memberTitle: "प्रीमियम सदस्य",
    memberDesc: "सक्रिय प्लान: {plan}",
    daysLeft: "{days} दिन शेष",
    tooltipFree: "प्रीमियम में अपग्रेड करें 👑",
    tooltipPremium: "प्रीमियम सक्रिय 🥇"
  },
  en_in: { // Hinglish
    premiumTitle: "Moxpal Premium",
    premiumDesc: "100% Ad-Free, GST Billing, Multi-device aur bohot kuch!",
    upgradeBtn: "Upgrade Karo 🚀",
    memberTitle: "Premium Member",
    memberDesc: "Active Plan: {plan}",
    daysLeft: "{days} Days Baki",
    tooltipFree: "Premium me upgrade karo 👑",
    tooltipPremium: "Premium Active 🥇"
  },
  gu: {
    premiumTitle: "મોક્સપાલ પ્રીમિયમ",
    premiumDesc: "100% જાહેરાત-મુક્ત, GST બિલિંગ, મલ્ટી-ડિવાઇસ!",
    upgradeBtn: "હમણાં અપગ્રેડ કરો 🚀",
    memberTitle: "પ્રીમિયમ સભ્ય",
    memberDesc: "સક્રિય પ્લાન: {plan}",
    daysLeft: "{days} દિવસ બાકી",
    tooltipFree: "પ્રીમિયમમાં અપગ્રેડ કરો 👑",
    tooltipPremium: "પ્રીમિયમ સક્રિય 🥇"
  },
  mr: {
    premiumTitle: "मोक्सपाल प्रीमियम",
    premiumDesc: "100% जाहिरात-मुक्त, GST बिलिंग, मल्टी-डिव्हाइस!",
    upgradeBtn: "आत्ता अपग्रेड करा 🚀",
    memberTitle: "प्रीमियम सदस्य",
    memberDesc: "सक्रिय प्लॅन: {plan}",
    daysLeft: "{days} दिवस शिल्लक",
    tooltipFree: "प्रीमियमवर अपग्रेड करा 👑",
    tooltipPremium: "प्रीमियम सक्रिय 🥇"
  },
  pa: {
    premiumTitle: "ਮੋਕਸਪਾਲ ਪ੍ਰੀਮੀਅਮ",
    premiumDesc: "100% ਵਿਗਿਆਪਨ-ਮੁਕਤ, GST ਬਿਲਿੰਗ, ਮਲਟੀ-ਡਵਾਈਸ!",
    upgradeBtn: "ਹੁਣੇ ਅੱਪਗ੍ਰੇਡ ਕਰੋ 🚀",
    memberTitle: "ਪ੍ਰੀਮੀਅਮ ਮੈਂਬਰ",
    memberDesc: "ਐਕਟਿਵ ਪਲਾਨ: {plan}",
    daysLeft: "{days} ਦਿਨ ਬਾਕੀ",
    tooltipFree: "ਪ੍ਰੀਮੀਅਮ ਵਿੱਚ ਅੱਪਗ੍ਰੇਡ ਕਰੋ 👑",
    tooltipPremium: "ਪ੍ਰੀਮੀਅਮ ਐਕਟਿਵ 🥇"
  },
  ta: {
    premiumTitle: "மோம்பட்ட பிரீமியம்",
    premiumDesc: "100% விளம்பரமற்றது, GST பில்லிங், மல்டி-டிவைஸ் மற்றும் பல!",
    upgradeBtn: "இப்போது மேம்படுத்தவும் 🚀",
    memberTitle: "பிரீமியம் உறுப்பினர்",
    memberDesc: "செயலில் உள்ள திட்டம்: {plan}",
    daysLeft: "{days} நாட்கள் மீதமுள்ளன",
    tooltipFree: "பிரீமியத்திற்கு மேம்படுத்தவும் 👑",
    tooltipPremium: "பிரீமியம் செயலில் உள்ளது 🥇"
  },
  te: {
    premiumTitle: "మోక్స్‌పాల్ ప్రీమియం",
    premiumDesc: "100% ప్రకటన రహిత, GST బిల్లింగ్, మల్టీ-డివైస్!",
    upgradeBtn: "ఇప్పుడే అప్‌గ్రేడ్ చేయండి 🚀",
    memberTitle: "ప్రీమియం సభ్యుడు",
    memberDesc: "యాక్టివ్ ప్లాన్: {plan}",
    daysLeft: "{days} రోజులు మిగిలి ఉన్నాయి",
    tooltipFree: "ప్రీమియంకు అప్‌గ్రేడ్ చేయండి 👑",
    tooltipPremium: "ప్రీమియం యాక్టివ్‌గా ఉంది 🥇"
  }
};

const planNames = {
  en: { 1: "Standard", 2: "Ads Free+", 3: "Premium" },
  hi: { 1: "स्टैंडर्ड", 2: "एड फ्री+", 3: "प्रीमियम" },
  en_in: { 1: "Standard", 2: "Ads Free+", 3: "Premium" },
  gu: { 1: "સ્ટાન્ડર્ડ", 2: "એડ ફ્રી+", 3: "પ્રીમિયમ" },
  mr: { 1: "स्टैंडर्ड", 2: "ॲड फ्री+", 3: "प्रीमियम" },
  pa: { 1: "ਸਟੈਂਡਰਡ", 2: "ਐਡ ਫ੍ਰੀ+", 3: "ਪ੍ਰੀਮੀਅਮ" },
  ta: { 1: "ஸ்டாண்டர்ட்", 2: "ஆட்ஸ் ஃப்ரீ+", 3: "பிரீமியம்" },
  te: { 1: "స్టాండర్డ్", 2: "యాడ్స్ ఫ్రీ+", 3: "ప్రీమియం" }
};

const menuTranslationKeys = {
  'Dashboard': LangKeys.home,
  'Parties': LangKeys.my_khata,
  'Cashbook': LangKeys.cashbook,
  'Bills': LangKeys.bills,
  'Items': LangKeys.items,
  'Reports': LangKeys.report,
  'Reminders': LangKeys.reminder,
  'Business Profile': LangKeys.business_settings,
  'Help & Support': LangKeys.help_support_title,
  'Settings': LangKeys.settings,
};

function Sidebar({
  activeTab,
  setActiveTab,
  isMobile = false,
  menuOpen = false,
  collapsed = false,
  setCollapsed,
  isPremium = false,
  premiumLevel = 0,
  premiumValidTill = null,
  premiumDaysLeft = null,
}) {
  const [hoveredItem, setHoveredItem] = useState(null)
  const [isToggleHovered, setIsToggleHovered] = useState(false)

  const { lang, t } = useLang()
  const currentLang = sidebarTranslations[lang] ? lang : 'en'
  const tLocal = (key) => sidebarTranslations[currentLang]?.[key] || sidebarTranslations.en[key]
  const activePlanName = planNames[currentLang]?.[premiumLevel] || planNames.en[premiumLevel] || ''

  const renderPremiumWidget = () => {
    // When collapsed, render a beautiful pulsing badge
    if (isCollapsed) {
      if (isPremium) {
        return (
          <div
            onClick={() => setActiveTab('Premium Plans')}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981, #059669)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              margin: '24px auto 12px auto',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              border: '2px solid rgba(16, 185, 129, 0.6)',
              animation: 'emeraldPulse 2s infinite',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            title={tLocal('tooltipPremium')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.15) rotate(10deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            }}
          >
            <PremiumMedalIcon size={26} />
          </div>
        );
      } else {
        return (
          <div
            onClick={() => setActiveTab('Premium Plans')}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              margin: '24px auto 12px auto',
              boxShadow: '0 4px 12px rgba(217, 119, 6, 0.3)',
              border: '2px solid rgba(245, 158, 11, 0.6)',
              animation: 'goldPulse 2s infinite',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            title={tLocal('tooltipFree')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.15) rotate(-10deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
            }}
          >
            <PremiumMedalIcon size={26} />
          </div>
        );
      }
    }

    // Expanded view
    if (isPremium) {
      // Active Premium Subscribed Card
      return (
        <div
          onClick={() => setActiveTab('Premium Plans')}
          style={{
            margin: '20px 0 16px 0',
            padding: '16px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(5, 150, 105, 0.03))',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.45)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.25)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'rgba(16, 185, 129, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <PremiumMedalIcon size={20} />
            </div>
            <div>
              <div style={{
                color: '#10B981',
                fontSize: '14px',
                fontWeight: '700',
                letterSpacing: '0.02em',
                fontFamily: "'Outfit', sans-serif",
              }}>
                {tLocal('memberTitle')}
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.65)',
                fontSize: '12px',
                fontWeight: '500',
                marginTop: '2px',
                fontFamily: "'Outfit', sans-serif",
              }}>
                {tLocal('memberDesc').replace('{plan}', activePlanName)}
              </div>
            </div>
          </div>
          {premiumDaysLeft !== null && (
            <div style={{
              marginTop: '12px',
              padding: '6px 12px',
              borderRadius: '8px',
              background: 'rgba(16, 185, 129, 0.15)',
              color: '#34D399',
              fontSize: '12px',
              fontWeight: '700',
              textAlign: 'center',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              fontFamily: "'Outfit', sans-serif",
            }}>
              {tLocal('daysLeft').replace('{days}', premiumDaysLeft)}
            </div>
          )}
        </div>
      );
    } else {
      // Free User Promotional Upgrade Card
      return (
        <div
          onClick={() => setActiveTab('Premium Plans')}
          style={{
            margin: '20px 0 16px 0',
            padding: '16px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.15), rgba(245, 158, 11, 0.04))',
            border: '1px solid rgba(217, 119, 6, 0.25)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.5)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(245, 158, 11, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(217, 119, 6, 0.25)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
          }}
        >
          {/* Decorative Sparkle */}
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            fontSize: '28px',
            opacity: 0.15,
          }}>
            ✨
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <PremiumMedalIcon size={20} />
            <span style={{
              color: '#F59E0B',
              fontSize: '14px',
              fontWeight: '700',
              letterSpacing: '0.03em',
              fontFamily: "'Outfit', sans-serif",
            }}>
              {tLocal('premiumTitle')}
            </span>
          </div>

          <p style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '11px',
            lineHeight: '1.4',
            margin: '0 0 12px 0',
            fontWeight: '500',
            fontFamily: "'Outfit', sans-serif",
          }}>
            {tLocal('premiumDesc')}
          </p>

          <button
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#FFFFFF',
              fontSize: '12px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 4px 10px rgba(217, 119, 6, 0.3)',
              transition: 'all 0.2s ease',
              fontFamily: "'Outfit', sans-serif",
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {tLocal('upgradeBtn')}
          </button>
        </div>
      );
    }
  };

  const menu = [
    { label: 'Dashboard', icon: LayoutDashboard },
    { label: 'Parties', icon: Users },
    { label: 'Cashbook', icon: Wallet },
    { label: 'Bills', icon: Receipt },
    { label: 'Items', icon: ItemsIcon },
    { label: 'Reports', icon: BarChart3 },
    { label: 'Reminders', icon: BellRing },
    { label: 'Business Profile', icon: Building2 },
    { label: 'Help & Support', icon: HelpCircle },
    { label: 'Settings', icon: Settings },
  ]

  const isCollapsed = !isMobile && collapsed

  return (
    <div
      className="no-print"
      style={{
        ...styles.sidebar,
        width: isMobile ? '280px' : (isCollapsed ? '80px' : '280px'),
        minWidth: isMobile ? '280px' : (isCollapsed ? '80px' : '280px'),
        maxWidth: isMobile ? '280px' : (isCollapsed ? '80px' : '280px'),
        transform: isMobile
          ? menuOpen
            ? 'translateX(0)'
            : 'translateX(-100%)'
          : 'translateX(0)',
        boxShadow: isMobile ? '0 10px 30px rgba(0,0,0,0.18)' : 'none',
      }}
    >
      {/* COLLAPSE TOGGLE (Desktop Only) */}
      {!isMobile && (
        <button
          style={{
            ...styles.collapseToggle,
            backgroundColor: isToggleHovered ? '#334155' : '#1E293B',
            transform: isToggleHovered ? 'translateY(-50%) scale(1.1)' : 'translateY(-50%) scale(1)',
          }}
          onClick={() => setCollapsed(!collapsed)}
          onMouseEnter={() => setIsToggleHovered(true)}
          onMouseLeave={() => setIsToggleHovered(false)}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      )}

      <div style={styles.inner}>
        <div style={styles.top}>
          {/* BRAND/LOGO SECTION */}
          <div style={{
            ...styles.brandCard,
            padding: isCollapsed ? '12px 0' : '14px',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: isCollapsed ? '0' : '12px',
          }}>
            <img
              src="/logo.png"
              alt="Moxpal"
              style={{
                ...styles.logo,
                width: isCollapsed ? '32px' : '40px',
                height: isCollapsed ? '32px' : '40px',
              }}
            />
            {!isCollapsed && (
              <div style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' }}>
                <div style={styles.brandTitle}>Moxpal Khata</div>
                <div style={styles.brandSub}>Business Web</div>
              </div>
            )}
          </div>

          {/* MENU ITEMS */}
          <div style={styles.menu}>
            {menu.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.label
              const isHovered = hoveredItem === item.label

              return (
                <div
                  key={item.label}
                  onClick={() => setActiveTab(item.label)}
                  onMouseEnter={() => setHoveredItem(item.label)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    ...styles.menuItem,
                    ...(isActive ? styles.activeMenu : {}),
                    ...(isHovered && !isActive ? styles.hoverMenu : {}),
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    padding: isCollapsed ? '0' : '0 16px',
                  }}
                  title={isCollapsed ? (t(menuTranslationKeys[item.label]) || item.label) : ''}
                >
                  <div style={{
                    ...styles.iconBox,
                    color: isActive ? '#FFFFFF' : (isHovered ? '#FFFFFF' : 'rgba(148, 163, 184, 0.7)'),
                  }}>
                    <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                  </div>

                  {!isCollapsed && (
                    <span style={{
                      opacity: isCollapsed ? 0 : 1,
                      whiteSpace: 'nowrap',
                      transition: 'opacity 0.2s',
                    }}>
                      {t(menuTranslationKeys[item.label]) || item.label}
                    </span>
                  )}

                  {isActive && !isCollapsed && (
                    <div style={styles.activeIndicator} />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* PREMIUM WIDGET */}
        {renderPremiumWidget()}

        {/* BOTTOM SECTION (Logout) */}
        <div style={styles.bottom}>
          <button
            style={{
              ...styles.logoutBtn,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              padding: isCollapsed ? '0' : '0 16px',
              backgroundColor: hoveredItem === 'Logout' ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
              color: hoveredItem === 'Logout' ? '#F87171' : 'rgba(148, 163, 184, 1)',
            }}
            onClick={() => setActiveTab('Logout')}
            onMouseEnter={() => setHoveredItem('Logout')}
            onMouseLeave={() => setHoveredItem(null)}
            title={isCollapsed ? (t(LangKeys.logout) || 'Logout') : ''}
          >
            <div style={{
              ...styles.iconBox,
              color: hoveredItem === 'Logout' ? '#F87171' : 'rgba(148, 163, 184, 0.7)',
            }}>
              <LogOut size={20} strokeWidth={2} />
            </div>
            {!isCollapsed && (
              <span style={{ opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' }}>
                {t(LangKeys.logout) || 'Logout'}
              </span>
            )}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes goldPulse {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5); }
          70% { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
        @keyframes emeraldPulse {
          0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
          70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
      `}</style>
    </div>
  )
}

const styles = {
  sidebar: {
    height: '100dvh',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)', // Premium Dark Theme
    zIndex: 1000,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRight: '1px solid rgba(255,255,255,0.05)',
  },

  inner: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 14px',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
  },

  collapseToggle: {
    position: 'absolute',
    right: '-12px',
    top: '32px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1001,
    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
    transition: 'all 0.2s ease',
    transform: 'translateY(-50%)',
  },

  top: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  brandCard: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '32px',
    transition: 'all 0.3s ease',
  },

  logo: {
    objectFit: 'contain',
    transition: 'all 0.3s ease',
  },

  brandTitle: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: '18px',
    letterSpacing: '-0.01em',
  },

  brandSub: {
    color: 'rgba(148, 163, 184, 0.8)', // Slate-400
    fontSize: '12px',
    marginTop: '1px',
    fontWeight: '500',
  },

  menu: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },

  menuItem: {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderRadius: '12px',
    color: 'rgba(148, 163, 184, 1)', // Slate-400
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
  },

  iconBox: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },

  activeMenu: {
    backgroundColor: 'rgba(59, 130, 246, 0.15)', // Blue-500 with opacity
    color: '#FFFFFF',
    fontWeight: '600',
  },

  hoverMenu: {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    color: 'rgba(255, 255, 255, 0.9)',
  },

  activeIndicator: {
    position: 'absolute',
    right: '0',
    width: '4px',
    height: '20px',
    backgroundColor: '#3B82F6', // Blue-500
    borderRadius: '4px 0 0 4px',
  },

  bottom: {
    paddingTop: '20px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },

  logoutBtn: {
    width: '100%',
    height: '48px',
    border: 'none',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    color: 'rgba(148, 163, 184, 1)',
    fontWeight: '500',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
}

export default Sidebar